export type ShoeSizeSystem = 'EUR' | 'UK' | 'US'

interface ShoeSizeMapping {
  EUR: number
  UK: number
  US: number
}

// Таблица соответствия размеров обуви (универсальная, подходит для большинства случаев)
const sizeMapping: ShoeSizeMapping[] = [
  { EUR: 35, UK: 2, US: 3 },
  { EUR: 35.5, UK: 2.5, US: 3.5 },
  { EUR: 36, UK: 3, US: 4 },
  { EUR: 36.5, UK: 3.5, US: 4.5 },
  { EUR: 37, UK: 4, US: 5 },
  { EUR: 37.5, UK: 4.5, US: 5.5 },
  { EUR: 38, UK: 5, US: 6 },
  { EUR: 38.5, UK: 5.5, US: 6.5 },
  { EUR: 39, UK: 6, US: 7 },
  { EUR: 39.5, UK: 6.5, US: 7.5 },
  { EUR: 40, UK: 7, US: 8 },
  { EUR: 40.5, UK: 7.5, US: 8.5 },
  { EUR: 41, UK: 8, US: 9 },
  { EUR: 41.5, UK: 8.5, US: 9.5 },
  { EUR: 42, UK: 9, US: 10 },
  { EUR: 42.5, UK: 9.5, US: 10.5 },
  { EUR: 43, UK: 10, US: 11 },
  { EUR: 43.5, UK: 10.5, US: 11.5 },
  { EUR: 44, UK: 11, US: 12 },
  { EUR: 44.5, UK: 11.5, US: 12.5 },
  { EUR: 45, UK: 12, US: 13 },
  { EUR: 45.5, UK: 12.5, US: 13.5 },
  { EUR: 46, UK: 13, US: 14 },
  { EUR: 46.5, UK: 13.5, US: 14.5 },
  { EUR: 47, UK: 14, US: 15 },
  { EUR: 48, UK: 15, US: 16 },
]

/**
 * Конвертирует размер обуви из одной системы в другую
 */
export function convertShoeSize(
  value: number,
  fromSystem: ShoeSizeSystem,
  toSystem: ShoeSizeSystem
): number | null {
  if (fromSystem === toSystem) return value

  // Находим ближайший размер в исходной системе
  const fromMapping = sizeMapping.find((m) => {
    const systemValue = m[fromSystem]
    return Math.abs(systemValue - value) < 0.1
  })

  if (!fromMapping) {
    // Если точного совпадения нет, ищем ближайший
    const closest = sizeMapping.reduce((prev, curr) => {
      const prevDiff = Math.abs(prev[fromSystem] - value)
      const currDiff = Math.abs(curr[fromSystem] - value)
      return currDiff < prevDiff ? curr : prev
    })
    return closest[toSystem]
  }

  return fromMapping[toSystem]
}

/**
 * Генерирует список размеров для указанной системы
 */
export function generateShoeSizes(system: ShoeSizeSystem): Array<{ value: number; label: string }> {
  // Получаем уникальные размеры для выбранной системы
  const sizes = new Set<number>()
  sizeMapping.forEach((mapping) => {
    sizes.add(mapping[system])
  })

  const sortedSizes = Array.from(sizes).sort((a, b) => a - b)

  return sortedSizes.map((size) => ({
    value: size,
    label: String(size)
  }))
}

/**
 * Получает диапазон размеров для указанной системы
 */
export function getShoeSizeRange(system: ShoeSizeSystem): { min: number; max: number; step: number } {
  const sizes = generateShoeSizes(system)
  const values = sizes.map((s) => s.value)
  const min = Math.min(...values)
  const max = Math.max(...values)

  // Определяем step на основе системы
  let step = 0.5
  if (system === 'EUR' || system === 'UK' || system === 'US') {
    // Проверяем, есть ли половинные размеры
    const hasHalfSizes = values.some((v) => v % 1 !== 0)
    step = hasHalfSizes ? 0.5 : 1
  }

  return { min, max, step }
}

